import { ChargeModuleDetailsModel, PackageModuleDetailModel, PackageModuleViewModel, PackageType } from "@admin/masters/pages/package-modules/models";
import { Component, Input, OnChanges, OnDestroy, OnInit, ViewEncapsulation } from "@angular/core";
import { ApiResources } from "@shared/helpers";
import { IResource, Page } from "@shared/models";
import { HttpService } from "@shared/services";
import { finalize, takeUntil } from "rxjs/operators";

@Component({
    templateUrl: "./view-package-module.html",
    selector: "view-package-module",
    encapsulation: ViewEncapsulation.None
})
export class ViewPackageModuleComponent implements OnInit, OnChanges, OnDestroy {
    packageType = PackageType;

    @Input() packageModuleId: number;
    @Input() isCounselling: boolean;
    @Input() chargeCategory!: IResource;
    @Input() hidePrice!: boolean;

    page: Page;
    loading: boolean;
    packageInfo: PackageModuleViewModel;
    loadingChargeCategories: boolean;
    chargeCategories: Array<IResource>;
    loadingCharges: boolean;
    charges: Array<ChargeModuleDetailsModel>;
    showRepeatType: boolean;
    hidePrices: boolean;

    constructor(private readonly httpService: HttpService) {
        this.page = new Page();
    }

    ngOnInit(): void {
        // this.findPackageInfo();
    }

    ngOnChanges(): void {
        this.findPackageInfo();
        this.hidePrices = this.isCounselling;
    }

    fetchPackageDetails(modulesMasterId: number): Array<PackageModuleDetailModel> {
        return this.packageInfo.packageModuleDetails.filter(m => m.modulesMasterId === modulesMasterId);
    }

    onSelectChargeCategory(chargeCategoryId: number): void {
        if (!this.chargeCategory)
            this.chargeCategory = this.chargeCategories.find(m => m.id === chargeCategoryId);

        this.fetchCharges();
    }

    onClearChargeCategory(): void {
        this.packageInfo.totalQuantity = 0;
        this.packageInfo.freeQuantity = 0;
        this.packageInfo.discount = 0;
        this.packageInfo.subTotal = 0;
        this.packageInfo.freeQuantityTotal = 0;
        this.packageInfo.total = 0;
    }

    ngOnDestroy(): void {
        this.page.unsubscribeAll();
    }

    private findPackageInfo(): void {
        this.loading = true;
        this.httpService
            .get(ApiResources.getURI(ApiResources.packageModule.base, ApiResources.packageModule.view), { packageModuleId: this.packageModuleId })
            .pipe(finalize(() => this.loading = false), takeUntil(this.page.unSubscribe))
            .subscribe({
                next: (response: PackageModuleViewModel) => {
                    this.packageInfo = response;
                    if (this.chargeCategory) {
                        this.setChareCategoryTotals(this.chargeCategory);
                        this.fetchCharges();
                        this.fetchChargeCategories();
                    }
                    else this.fetchChargeCategories();
                },
                error: () => this.packageInfo = undefined
            });
    }

    private fetchChargeCategories(): void {
        const requestBody = {
            chargeModuleTemplateId: this.packageInfo.packageModule.chargeModuleTemplateId,
            locationId: this.packageInfo.packageModule.locationId,
            packageModuleId: this.packageInfo.packageModule.packageModuleId
        };

        this.loadingChargeCategories = true;
        this.httpService
            .get(ApiResources.getURI(ApiResources.packageModule.base, ApiResources.packageModule.fetchChargeCategoriesWithTotal), requestBody)
            .pipe(finalize(() => this.loadingChargeCategories = false), takeUntil(this.page.unSubscribe))
            .subscribe({
                next: (chargeCategories: Array<IResource>) => {
                    if (chargeCategories.length) {
                        chargeCategories.forEach((chargeCategory: IResource) => {
                            this.setChareCategoryTotals(chargeCategory);
                        });
                    }

                    this.chargeCategories = chargeCategories;
                    if (this.chargeCategory) {
                        this.chargeCategory = this.chargeCategories.find(s => s.id == this.chargeCategory.id);
                    }
                },
                error: () => this.chargeCategories = []
            });
    }

    private setChareCategoryTotals(chargeCategory: IResource): void {
        let subTotal = chargeCategory.value ? parseFloat(chargeCategory.value) : 0, discount = 0, total = subTotal;
        if (this.packageInfo.packageModule.discountType === "P") {
            discount = parseFloat((((subTotal * (this.packageInfo.packageModule.discountPercentage || 0)) / 100) || 0).toFixed(2));
            discount = isNaN(discount) ? 0 : discount;
            total = subTotal - discount;
            total = total > 0 ? total : 0;
        } else if (this.packageInfo.packageModule.discountType === "A") {
            discount = this.packageInfo.packageModule.discountAmount || 0;
            discount = isNaN(discount) ? 0 : discount;
            total = subTotal - discount;
            total = total > 0 ? total : 0;
        }

        chargeCategory.subTotal = subTotal;
        chargeCategory.discount = discount;
        chargeCategory.total = total;
    }

    private fetchCharges(): void {
        const requestBody = {
            chargeCategoryId: this.chargeCategory.id,
            chargeModuleTemplateId: this.packageInfo.packageModule.chargeModuleTemplateId,
            locationId: this.packageInfo.packageModule.locationId,
            packageModuleId: this.packageInfo.packageModule.packageModuleId,
            modulesMasterIds: this.packageInfo.packageModule.modulesMasterIds
        };

        this.loadingCharges = true;
        this.httpService
            .post(ApiResources.getURI(ApiResources.packageModule.base, ApiResources.packageModule.fetchChargeDetails), requestBody)
            .pipe(finalize(() => this.loadingCharges = false), takeUntil(this.page.unSubscribe))
            .subscribe({
                next: (charges: Array<ChargeModuleDetailsModel>) => {
                    this.charges = charges;
                    if (charges && charges.length) {
                        this.showRepeatType = charges[0].repeatTypeId > 0;
                        this.packageInfo.packageModuleDetails.forEach(m => {
                            if (m.referenceId) {
                                const charge = charges.find(n => n.modulesMasterId === m.modulesMasterId && n.referenceId === m.referenceId);
                                if (charge) {
                                    m.isNA = false;
                                    m.amount = charge.amount;
                                }
                                else
                                    m.isNA = true;
                            } else
                                m.isNA = false;
                        });

                        const packageDetails = this.packageInfo.packageModuleDetails.filter(m => !m.isNA);
                        const freePackageDetails = this.packageInfo.packageModuleDetails.filter(m => !m.isNA && m.isFree);
                        this.packageInfo.totalQuantity = packageDetails.map(m => m.quantity).reduce((sum, current) => sum + current, 0);
                        this.packageInfo.freeQuantity = freePackageDetails.map(m => m.quantity).reduce((sum, current) => sum + current, 0);
                        this.packageInfo.discount = 0;
                        this.packageInfo.subTotal = packageDetails.map(m => m.amount).reduce((sum, current) => sum + current, 0);
                        this.packageInfo.freeQuantityTotal = freePackageDetails.map(m => m.amount).reduce((sum, current) => sum + current, 0);
                        this.packageInfo.total = this.packageInfo.subTotal - this.packageInfo.freeQuantityTotal;
                        this.packageInfo.subTotal = this.packageInfo.total;
                    }
                },
                error: () => this.charges = []
            });
    }
}